import Config from '@config';
import { CACHE_EXPIRATION_TIME } from '@gasbi/shared/common/features/constants';
import {
  FiltersGrid,
  Pagination,
  infiniteQueryOptions,
  normalizeInfiniteQueryData,
} from '@gasbi/shared/index';
import { createApi } from '@reduxjs/toolkit/query/react';

import axiosBaseQuery from '@/utils/axiosBaseQuery';

import {
  PaymentImport,
  PaymentImportRowError,
  UploadPaymentImportRequest,
  UploadPaymentImportResponse,
} from './types';

export const paymentImportsApi = createApi({
  reducerPath: 'paymentImportsApi',
  baseQuery: axiosBaseQuery({
    baseUrl: Config.VITE_API_URL + '/payment-imports',
  }),
  tagTypes: ['PaymentImports'],
  keepUnusedDataFor: CACHE_EXPIRATION_TIME,
  endpoints: builder => ({
    getPaymentImportsI: builder.infiniteQuery<
      PaymentImport[],
      FiltersGrid | void,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg, pageParam }) => ({
        url: '',
        params: { ...queryArg, ...pageParam },
      }),
      providesTags: () => ['PaymentImports'],
    }),
    getPaymentImportById: builder.query<PaymentImport, string>({
      query: importId => ({ url: `/${importId}` }),
      providesTags: (_result, _error, importId) => [
        { type: 'PaymentImports', id: importId },
      ],
    }),
    getPaymentImportRowErrorsI: builder.infiniteQuery<
      PaymentImportRowError[],
      { importId: string } & FiltersGrid,
      Pagination
    >({
      infiniteQueryOptions,
      query: ({ queryArg: { importId, ...filters }, pageParam }) => ({
        url: `/${importId}/errors`,
        params: { ...filters, ...pageParam },
      }),
      providesTags: (_result, _error, { importId }) => [
        { type: 'PaymentImports', id: importId },
      ],
    }),
    uploadPaymentImport: builder.mutation<
      UploadPaymentImportResponse,
      UploadPaymentImportRequest
    >({
      query: ({ file }) => {
        const formData = new FormData();
        formData.append('file', file, file.name);

        return {
          url: '',
          method: 'POST',
          data: formData,
          headers: { 'Content-Type': 'multipart/form-data' },
        };
      },
      invalidatesTags: ['PaymentImports'],
    }),
  }),
});

export const { useUploadPaymentImportMutation, useGetPaymentImportByIdQuery } =
  paymentImportsApi;

export const useGetPaymentImportsInfiniteQuery = normalizeInfiniteQueryData(
  paymentImportsApi.useGetPaymentImportsIInfiniteQuery,
);

export const useGetPaymentImportRowErrorsInfiniteQuery =
  normalizeInfiniteQueryData(
    paymentImportsApi.useGetPaymentImportRowErrorsIInfiniteQuery,
  );
